import React, { useState, useEffect } from 'react';
import { glassSurface, viGlassFill, viGlassBorder, viGlassShadow } from './config/glassTheme';
import { useNavigate } from 'react-router-dom';
import API from './api';
import ProductNav from './components/ProductNav';
import GuardedCheckoutConsent, { useGuardedCheckout } from './components/GuardedCheckout';

const CREDIT_PACKS = [
  {
    id: 'starter',
    name: 'Starter',
    credits: 5,
    price: 5.99,
    equiv: '5 x 5-sec animations',
    highlight: false,
  },
  {
    id: 'standard',
    name: 'Standard',
    credits: 10,
    price: 11.99,
    equiv: '10 x 5-sec or 5 x 10-sec',
    highlight: true,
  },
  {
    id: 'pro',
    name: 'Pro',
    credits: 20,
    price: 21.99,
    equiv: '10 x 10-sec animations',
    highlight: false,
  },
];

export default function AnimationCreditsPage() {
  const navigate                          = useNavigate();
  const [animCredits,     setAnimCredits] = useState(null);
  const [purchaseLoading, setPurchaseLoading] = useState(null);
  const [error,           setError]       = useState('');

  useEffect(() => {
    API.get('/v1/animation/credits')
      .then(res => setAnimCredits(res.data?.balance ?? 0))
      .catch(() => setAnimCredits(0));
  }, []);

  // Shared consent controller. The SERVER decides whether an acknowledgement is required; if it
  // refuses the checkout pending one, this renders the server's own wording and retries.
  const consentCheckout = useGuardedCheckout();

  async function handlePurchase(pack) {
    setPurchaseLoading(pack.id);
    setError('');
    try {
      const res = await consentCheckout.start(
        () => API.post('/v1/animation/credits/purchase', { pack_id: pack.id }),
        { purchaseType: 'animation_credits', intendedSelector: pack.id });
      // null means the controller is now showing the acknowledgement instead of an error.
      if (res && res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } finally {
      setPurchaseLoading(null);
    }
  }

  return (
    <div style={s.page}>
      <ProductNav />

      <div style={s.container}>

        {/* Page header */}
        <div style={s.pageHeader}>
          <div>
            <h1 style={s.pageTitle}>Animation Credits</h1>
            <p style={s.pageSub}>Purchase credits to animate your generated images. Pay-as-you-go - no subscription required.</p>
          </div>
          <div style={s.creditPill}>
            <span style={s.creditPillIcon}>▶</span>
            <span style={s.creditPillLabel}>Current Balance:</span>
            <span style={s.creditPillVal}>{animCredits === null ? '-' : animCredits}</span>
            <span style={s.creditPillUnit}>credits</span>
          </div>
        </div>

        {/* How it works strip */}
        <div style={s.howItWorks}>
          <div style={s.howItem}>
            <div style={s.howIcon}>🖼️</div>
            <div style={s.howText}>Generate an image using any VISINVOKE engine</div>
          </div>
          <div style={s.howArrow}>→</div>
          <div style={s.howItem}>
            <div style={s.howIcon}>▶</div>
            <div style={s.howText}>Click "Animate This" on any generated image</div>
          </div>
          <div style={s.howArrow}>→</div>
          <div style={s.howItem}>
            <div style={s.howIcon}>🎬</div>
            <div style={s.howText}>Choose 5-sec (1 credit) or 10-sec (2 credits)</div>
          </div>
          <div style={s.howArrow}>→</div>
          <div style={s.howItem}>
            <div style={s.howIcon}>⬇</div>
            <div style={s.howText}>Download your animation as MP4</div>
          </div>
        </div>

        {error && <div style={s.errorBox}>{error}</div>}

        {/* Packs */}
        <div style={s.sectionTitle}>Choose a Credit Pack</div>
        <div style={s.creditKey}>1 credit = 5 seconds &nbsp;•&nbsp; 2 credits = 10 seconds</div>
        {/* Rendered ONLY when the server refuses the checkout pending an acknowledgement. */}
        <GuardedCheckoutConsent
          checkout={consentCheckout}
          onDone={(res) => {
            if (res && res.data?.checkout_url) window.location.href = res.data.checkout_url;
          }}
        />

        <div style={s.packsGrid}>
          {CREDIT_PACKS.map(pack => (
            <div
              key={pack.id}
              style={{
                ...s.packCard,
                borderColor: pack.highlight ? '#15803d' : 'rgba(255,255,255,0.12)',
                boxShadow: pack.highlight
                  ? '0 0 0 2px #15803d44, 0 8px 32px rgba(0,0,0,0.2)'
                  : '0 8px 32px rgba(0,0,0,0.2)',
              }}
            >
              {pack.highlight && (
                <div style={s.popularBadge}>Most Popular</div>
              )}
              <div style={s.packName}>{pack.name}</div>
              <div style={s.packCredits}>
                <span style={s.packCreditsNum}>{pack.credits}</span>
                <span style={s.packCreditsLabel}>credits</span>
              </div>
              <div style={s.packEquiv}>{pack.equiv}</div>
              <div style={s.packPrice}>
                <span style={s.packPriceNum}>${pack.price.toFixed(2)}</span>
                <span style={s.packPriceLabel}>USD</span>
              </div>
              <button
                style={{
                  ...s.packBtn,
                  background: pack.highlight
                    ? 'linear-gradient(135deg, #15803d, #16a34a)'
                    : 'linear-gradient(135deg, #374151, #4b5563)',
                  opacity: purchaseLoading === pack.id ? 0.6 : 1,
                }}
                onClick={() => handlePurchase(pack)}
                disabled={purchaseLoading === pack.id}
              >
                {purchaseLoading === pack.id ? 'Loading...' : 'Buy Credits'}
              </button>
            </div>
          ))}
        </div>

        <div style={s.purchaseNote}>
          Credits are consumed at generation time. 5-second animations cost 1 credit. 10-second animations cost 2 credits. Credits never expire.
        </div>

        {/* CTA to animate */}
        {animCredits !== null && animCredits > 0 && (
          <div style={s.ctaBox}>
            <div style={s.ctaText}>You have <strong>{animCredits} credit{animCredits !== 1 ? 's' : ''}</strong> ready to use.</div>
            <button style={s.ctaBtn} onClick={() => navigate('/workspace')}>Go Animate an Image</button>
          </div>
        )}

      </div>
    </div>
  );
}

const s = {
  page:      { minHeight: '100vh', background: 'linear-gradient(135deg, #1e1b4b 0%, #334155 50%, #881337 100%)', fontFamily: "'Segoe UI', sans-serif", color: '#fff' },
  container: { maxWidth: 1100, margin: '0 auto', padding: '32px 40px' },

  pageHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, marginBottom: 28, flexWrap: 'wrap' },
  pageTitle:  { color: '#fff', fontSize: 30, fontWeight: 800, margin: '0 0 6px' },
  pageSub:    { color: 'rgba(255,255,255,0.6)', fontSize: 15, margin: 0, maxWidth: 560 },

  creditPill:      { display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 30, padding: '10px 20px', flexShrink: 0 },
  creditPillIcon:  { fontSize: 14, color: '#4ade80' },
  creditPillLabel: { fontSize: 13, color: 'rgba(255,255,255,0.6)', fontWeight: 600 },
  creditPillVal:   { fontSize: 22, fontWeight: 900, color: '#fff', lineHeight: 1 },
  creditPillUnit:  { fontSize: 13, color: 'rgba(255,255,255,0.5)' },

  howItWorks: { display: 'flex', alignItems: 'center', gap: 12, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: '20px 28px', marginBottom: 32, flexWrap: 'wrap', justifyContent: 'center' },
  howItem:    { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, maxWidth: 160, textAlign: 'center' },
  howIcon:    { fontSize: 28, lineHeight: 1 },
  howText:    { fontSize: 13, color: 'rgba(255,255,255,0.65)', lineHeight: 1.4 },
  howArrow:   { fontSize: 20, color: 'rgba(255,255,255,0.25)', flexShrink: 0 },

  errorBox:    { background: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 8, padding: '12px 16px', fontSize: 13, marginBottom: 20 },
  sectionTitle: { color: '#fff', fontWeight: 800, fontSize: 20, marginBottom: 8 },
  creditKey:    { color: 'rgba(255,255,255,0.5)', fontSize: 13, marginBottom: 20 },

  packsGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24, marginBottom: 20 },

  packCard: { background: 'rgba(255,255,255,0.97)', borderRadius: 16, padding: '32px 28px', border: '2px solid rgba(255,255,255,0.12)', display: 'flex', flexDirection: 'column', gap: 10, position: 'relative', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' },
  popularBadge: { position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(135deg, #15803d, #16a34a)', color: '#fff', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '4px 14px', borderRadius: 20, whiteSpace: 'nowrap' },
  packName:         { fontWeight: 800, fontSize: 18, color: '#1a1a1a' },
  packCredits:      { display: 'flex', alignItems: 'baseline', gap: 6 },
  packCreditsNum:   { fontWeight: 900, fontSize: 44, color: '#1a1a1a', lineHeight: 1 },
  packCreditsLabel: { fontSize: 15, color: '#6b7280', fontWeight: 600 },
  packEquiv:        { fontSize: 13, color: '#9ca3af', lineHeight: 1.4 },
  packPrice:        { display: 'flex', alignItems: 'baseline', gap: 4, marginTop: 6 },
  packPriceNum:     { fontWeight: 900, fontSize: 28, color: '#1a1a1a' },
  packPriceLabel:   { fontSize: 13, color: '#9ca3af' },
  packBtn: { color: '#fff', border: 'none', borderRadius: 10, padding: '13px 0', fontWeight: 700, fontSize: 14, cursor: 'pointer', width: '100%', marginTop: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.18)', transition: 'opacity 0.15s' },

  purchaseNote: { color: 'rgba(255,255,255,0.35)', fontSize: 12, fontStyle: 'italic', textAlign: 'center', padding: '16px 0', borderTop: '1px solid rgba(255,255,255,0.08)', marginBottom: 24 },

  ctaBox:  { background: viGlassFill('#0E7A50', '#16a34a', '#4ADE80'), border: viGlassBorder('#4ADE80', 1.5), boxShadow: viGlassShadow('#0E7A50', '#16a34a', '#4ADE80', false), borderRadius: 14, padding: '20px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' },
  ctaText: { color: 'rgba(255,255,255,0.85)', fontSize: 15 },
  ctaBtn:  { ...glassSurface('#0E7A50', '#16a34a', '#4ADE80'), borderRadius: 10, padding: '12px 24px', fontWeight: 700, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap' },
};
